// Groups a rider's score-sheet-portal rides by day, then orders each day's
// rides by ride time (#1699 — the flat list gave a multi-day rider no
// structure: CL 122 on 16 Jul followed straight by CL 223 on 17 Jul with
// nothing marking the day change).
//
// 🔴 Day-key sourcing: the public score-sheets API (report-api's
// GET /public/score-sheets/:token) never hands score-fe a raw UTC
// timestamp — only `ride.date`/`ride.time`, already rendered venue-local by
// report-defs' shortDate()/shortTime() (hardcoded America/Los_Angeles, the
// same "stamp UTC, render venue-local" #688 rule common-fe/venueTime's
// venueDayKey() codifies for the scoreboard family, and the same rule card
// #1559 had to retrofit into scribe after a naive UTC-slice put an evening
// ride under the wrong day). SheetsPage.jsx's own RideCard already
// establishes the rule for this component: reuse those exact strings
// rather than re-deriving anything from them (see its comment above
// `dateTimeBits`) — re-parsing here would risk re-implementing that exact
// bug a third time, off a value that isn't even a raw instant.
//
// So this groups on `ride.date` verbatim (an opaque, already-correct label
// — never reformatted, never fed through a Date/timeZone conversion) and
// only parses `ride.date` / `ride.time` to build a numeric SORT key. No
// timezone math happens in this file at all; the venue-local computation
// already happened upstream, once, in report-defs.

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// report-defs' shortDate() itself renders en-GB "16 Jul 2026" (2-digit day,
// short month, 4-digit year); this component's own #944 comment (see
// RideCard's `dateTimeBits`) instead documents the shape as en-US "Jul 16,
// 2026". Rather than bet on which one is actually live, accept both — this
// still never re-derives a DISPLAYED value, it only builds a numeric SORT
// key, and a format this doesn't recognize degrades to the "Other" bucket
// (see groupRidesByDay) instead of miscomputing a wrong day order.
function dateSortKey(dateStr) {
  const s = String(dateStr || "").trim();
  let m = /^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/.exec(s); // "16 Jul 2026"
  if (m) {
    const day = Number(m[1]);
    const monthIndex = MONTHS.indexOf(m[2]);
    const year = Number(m[3]);
    if (monthIndex >= 0 && Number.isFinite(day) && Number.isFinite(year)) {
      return year * 10000 + (monthIndex + 1) * 100 + day;
    }
  }
  m = /^([A-Za-z]{3})\s+(\d{1,2}),\s*(\d{4})$/.exec(s); // "Jul 16, 2026"
  if (m) {
    const monthIndex = MONTHS.indexOf(m[1]);
    const day = Number(m[2]);
    const year = Number(m[3]);
    if (monthIndex >= 0 && Number.isFinite(day) && Number.isFinite(year)) {
      return year * 10000 + (monthIndex + 1) * 100 + day;
    }
  }
  return null;
}

// report-defs shortTime() shape: en-US "8:10 AM" / "11:49 PM" (no leading
// zero on the hour, 2-digit minute). Returns minutes-since-midnight, or
// null if unparseable.
function timeSortKey(timeStr) {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(String(timeStr || "").trim());
  if (!m) return null;
  let hour = Number(m[1]) % 12;
  if (/pm/i.test(m[3])) hour += 12;
  return hour * 60 + Number(m[2]);
}

const UNDATED_KEY = "__undated__";

// Returns [{ dayKey, heading, rides }], days in chronological order, each
// `rides` array sorted by ride time ascending. `dayKey` is `ride.date`
// itself for a real day (so display never diverges from what the ride card
// already shows) and `null` for the trailing "Other" bucket a ride with no
// parseable date falls into — back-compat rows can be missing `date`
// entirely; this must degrade instead of dropping the ride or crashing.
export function groupRidesByDay(rides) {
  const byKey = new Map();
  for (const ride of rides || []) {
    const hasDate = ride?.date && dateSortKey(ride.date) !== null;
    const key = hasDate ? ride.date : UNDATED_KEY;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(ride);
  }

  const entries = [...byKey.entries()];
  entries.sort(([keyA], [keyB]) => {
    const a = keyA === UNDATED_KEY ? Infinity : dateSortKey(keyA);
    const b = keyB === UNDATED_KEY ? Infinity : dateSortKey(keyB);
    return a - b;
  });

  return entries.map(([key, dayRides]) => ({
    dayKey: key === UNDATED_KEY ? null : key,
    heading: key === UNDATED_KEY ? "Other" : key,
    rides: [...dayRides].sort((a, b) => {
      const at = timeSortKey(a?.time);
      const bt = timeSortKey(b?.time);
      if (at === null && bt === null) return 0;
      if (at === null) return 1;
      if (bt === null) return -1;
      return at - bt;
    }),
  }));
}

export default groupRidesByDay;
